import React, { useCallback } from 'react';
import { Leaf, ArrowLeft, CheckCircle, Star, TrendingUp, Filter, Users } from 'lucide-react';

const PRICING_TIERS = [
  {
    name: "Free Preview",
    price: "$0",
    period: "forever",
    description: "Get a taste of what WeederBoard offers",
    features: [
      "View top 10 daily deals",
      "Filter offers by State"
    ],
    buttonText: "Get Started Free",
    buttonStyle: "border-2 border-gray-300 text-gray-700 hover:bg-gray-50",
    popular: false,
    limitations: [
      "Access to top deals leaderboard only",
      "No search functionality",
      "No farm, strain, and brand filtering"
    ]
  },
  {
    name: "Premium",
    price: "$4.20",
    period: "per month",
    description: "Full access to all deals and features",
    features: [
      "Unlimited deal access",
      "Search by strain, brand, or dispensary",
      "Filter by farm, strain, weight, concentrate type, location or dispensary",
      "Find all the dispensaries in your area and compare their offerings",
      "Mobile-optimized experience",
      "Coming soon: Community reviews, Personalized Leaderboards, Custom Deal Alerts And More!"
    ],
    buttonText: "Start Saving Today!",
    buttonStyle: "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg",
    popular: true,
    savings: "Save big on cannabis purchases!"
  }
];


const PricingCard = React.memo(({ tier, onSelectPlan }) => {
  const handleSelectPlan = useCallback(() => {
    onSelectPlan(tier);
  }, [tier, onSelectPlan]);

  return (
    <div className={`border-2 rounded-xl p-8 shadow-lg transition-all duration-300 hover:shadow-xl relative ${
      tier.popular 
        ? 'border-green-500 scale-105 lg:scale-110' 
        : 'border-gray-200 hover:border-green-300'
    }`}>
      {tier.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center space-x-1">
            <Star size={16} />
            <span>Most Popular</span>
          </div>
        </div>
      )}
      
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
        <div className="mb-4">
          <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
          {tier.price !== "$0" && <span className="text-gray-500 ml-2">{tier.period}</span>}
        </div>
        <p className="text-gray-600">{tier.description}</p>
        {tier.savings && (
          <p className="text-green-600 font-semibold mt-2">{tier.savings}</p>
        )}
      </div>
      
      <ul className="space-y-4 mb-8">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-3">
            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      {tier.limitations && (
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-gray-500 mb-3">Limitations:</h4>
          <ul className="space-y-2">
            {tier.limitations.map((limitation, index) => (
              <li key={index} className="flex items-start space-x-3">
                <span className="text-red-400 flex-shrink-0 mt-0.5">•</span>
                <span className="text-sm text-gray-600">{limitation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <button
        onClick={handleSelectPlan}
        className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${tier.buttonStyle}`}
      >
        {tier.buttonText}
      </button>
    </div>
  );
});

const Pricing = ({ onSelectPlan, onBack }) => {
  const handleSelectPlan = useCallback((plan) => {
    if (plan.name === 'Free Preview') {
      onSelectPlan('free');
    } else if (plan.name === 'Premium') {
      onSelectPlan('premium');
    }
  }, [onSelectPlan]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back</span>
              </button>
              <div className="flex items-center space-x-2">
                <Leaf className="text-green-600" size={32} />
                <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  WeederBoard
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Choose the plan that works best for you. Upgrade or downgrade at any time.
            </p>
            
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {PRICING_TIERS.map((tier, index) => (
              <PricingCard 
                key={index} 
                tier={tier} 
                onSelectPlan={handleSelectPlan}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Premium?
              </h2>
              <p className="text-xl text-gray-600">
                Premium members save significantly more than the cost of subscription
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-200 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="text-green-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Track All Deals</h3>
                <p className="text-gray-600">
                  Access hundreds of deals across all dispensaries, updated multiple times daily
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-200 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Filter className="text-green-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Smart Filtering</h3>
                <p className="text-gray-600">
                  Find your favorite strains, shop your favorite brands, and see what your favorite dispensaries have to offer.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-200 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="text-green-600" size={24} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Join the Community</h3>
                <p className="text-gray-600">
                  Join thousands of smart cannabis users saving money daily!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Pricing FAQ
              </h2>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600">
                  Yes! You can cancel your premium subscription at any time through your account settings. No long-term contracts or cancellation fees.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2">How much can I really save?</h3>
                <p className="text-gray-600">
                  Our premium members save an average of $127 per month by finding better deals. The subscription pays for itself with just one good deal!
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2">Can i get a discount??</h3>
                <p className="text-gray-600">
                  We will soon be offering a yearly subscription for a discounted rate of $42.00 per year.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Ready to Start Saving?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Join thousands of cannabis consumers who are already saving money with WeederBoard.
            </p>
            <button
              onClick={() => handleSelectPlan({ name: 'Premium' })}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              Start Premium Today - $4.20/month
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Leaf className="text-green-400" size={24} />
            <span className="text-xl font-bold">WeederBoard</span>
          </div>
          <p className="text-gray-400">
            The smartest way to find deals on cannabis products and save money.
          </p>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <p className="text-gray-400">&copy; 2025 WeederBoard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
